import { useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { Breadcrumbs } from '@loadsmart/loadsmart-ui'
import { Button, Layout } from '@loadsmart/miranda-react'

import { type RequestStatus } from 'core/domain/Request'
import { SHIPPER_GUIDE_API_URL } from 'core/infra/environment'
import { parseQueryString, stringifyQueryString } from 'core/infra/queryString'
import { Icon } from 'core/ui/components/icon/Icon'

import user from 'legacy/utils/user'

import { isTenderLoad, type Load } from '../domain/Load'
import { LoadAttachmentList } from './LoadAttachmentList'
import { LoadExtraInformation } from './LoadExtraInformation'
import { LoadHeader } from './LoadHeader'
import { LoadInformation } from './LoadInformation'
import { LoadMap } from './LoadMap'

const LOADBOARD_URL = '/#/portal/bids-list'

export type LoadDetailProps = {
  readonly load: Load
  readonly onAccept?: () => void
  readonly onReject?: () => void
  readonly onAttachmentDownload: (attachmentId: number) => void
  readonly statusAttachment?: RequestStatus
}

export function LoadDetail({
  load,
  onAccept,
  onReject,
  onAttachmentDownload,
  statusAttachment,
}: LoadDetailProps) {
  const { search } = useLocation()
  const { push: navigate, replace } = useHistory()

  const isTender = isTenderLoad(load)

  const handleDownloadTenderDetailsPdf = useCallback(() => {
    window.open(`${SHIPPER_GUIDE_API_URL}rates/${load.rateUUID}/confirmation.pdf`, 'blank')
  }, [load])

  const handleBackToLoadboard = useCallback(() => {
    navigate(`/portal/bids-list/`)
  }, [navigate])

  const breadcrumbEntries = useMemo(
    () => [
      {
        label: 'Tenders',
        onClick: handleBackToLoadboard,
        url: LOADBOARD_URL,
      },
      { label: `${load.shipper.companyName}'s Tender Details` },
    ],
    [handleBackToLoadboard, load]
  )

  function handleUnsignedClick(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (!user.isLoggedIn()) {
      e.stopPropagation()
      e.preventDefault()

      replace({ search: stringifyQueryString({ ...parseQueryString(search), signup: 'true' }) })
    }
  }

  return (
    <Layout.Stack paddingT="spacing-4" gap="spacing-6">
      <Layout.Group gap="spacing-0-5" justify="space-between" align="center">
        <div onClickCapture={handleUnsignedClick}>
          <Breadcrumbs onBack={handleBackToLoadboard} entries={breadcrumbEntries} />
        </div>
        <Layout.Group gap="spacing-0-5" style={{ flexGrow: 1 }}>
          {isTender && (
            <Button
              type="button"
              variant="tertiary"
              leading={<Icon name="download" />}
              onClick={handleDownloadTenderDetailsPdf}
              style={{ marginLeft: 'auto' }}
            >
              Rate confirmation
            </Button>
          )}
        </Layout.Group>
      </Layout.Group>
      <LoadHeader load={load} onAccept={onAccept} onReject={onReject} />
      <Layout.Group gap="spacing-6">
        <LoadInformation load={load} />
        <LoadMap load={load} />
      </Layout.Group>
      <LoadExtraInformation load={load} />
      <LoadAttachmentList
        load={load}
        onAttachmentDownload={onAttachmentDownload}
        statusAttachment={statusAttachment}
      />
    </Layout.Stack>
  )
}
